import { useEffect, useState } from 'react';
import {
  Activity, BookOpen, Bot, ChevronLeft, Cpu, GraduationCap, KeyRound,
  ChevronsRight, Layers3, Search, StudiumHeaderWordmark, StudiumLogo, ShieldCheck, Sparkles, Target, UserCheck,
} from './Glyphs';
import PrivacyPolicy from './PrivacyPolicy';

/* The public face of Studium. Everything here is content a signed-out visitor
   may read, so it states only what the system already does. The track catalogue
   is deliberately honest: one track is live, the rest are marked as being
   built, with no dates attached. */

type Fact = { icon: (props: { size?: number }) => React.JSX.Element; title: string; body: string };

const STATS = [
  { value: '40', label: 'יחידות לימוד', note: '32 שיעורים ו־8 מעבדות' },
  { value: '8', label: 'שבועות מובנים', note: 'כל שבוע נסגר במעבדה' },
  { value: '720', label: 'שאלות מבוססות־מקור', note: 'ו־200 מושגי ליבה' },
  { value: '840', label: 'מקטעי ידע קנוני', note: 'עם מקור מדויק לכל תשובה' },
];

const PILLARS: Fact[] = [
  {
    icon: GraduationCap,
    title: 'אקדמיה מובנית',
    body: 'כל יחידת לימוד עוברת שישה שלבי התקדמות — תדריך, מושגים, דוגמה מפורטת, תרגול, שער שליטה ותוצר. המעבר בין השלבים נאכף בשרת ולא בדפדפן.',
  },
  {
    icon: BookOpen,
    title: 'ידע קנוני',
    body: 'מאגר הידע הוא מקור האמת היחיד לחומר הלימוד. כל תשובה חוזרת עם המקטע המדויק שעליו היא נשענת, וניתן לפתוח אותו במקום.',
  },
  {
    icon: Bot,
    title: 'עוזר AI אישי',
    body: 'לכל לומד מאושר מודל AI משלו, שנבחר מקטלוג מודלים מלא. השיחות, המחקר וההיסטוריה שמורים בנפרד לכל חשבון.',
  },
  {
    icon: Activity,
    title: 'מדידה והתקדמות',
    body: 'נקודות ניסיון, רמות, רצפי למידה ושמונה סמלי הישג. ההתקדמות מחושבת ונשמרת בשרת, כך שהיא נשארת מדויקת בין מכשירים.',
  },
];

const ACTIVE_TRACK = {
  title: 'מנהל/ת פרויקטי AI',
  summary: 'המסלול מחבר שליטה בנתונים, שכבה סמנטית, Text-to-SQL, סוכנים, אבטחה והובלה מוצרית לנכס מקצועי אחד.',
  weeks: [
    'יסודות SQL, אגרגציות, Joins ופונקציות חלון',
    'BigQuery: ארכיטקטורה, ביצועים ועלות',
    'מידול נתונים, מדדים ושכבה סמנטית',
    'Text-to-SQL: אחזור, יצירה ואימות',
    'Vertex AI, Function Calling וסוכנים ב־ADK',
    'אבטחה, הרשאות ומודל איומים',
    'Evaluation, ניטור ותהליך גרסאות',
    'הובלה מוצרית ותקשורת הנהלה',
  ],
};

const PLANNED_TRACKS = [
  { title: 'מהנדס/ת דאטה', body: 'צינורות נתונים, איכות נתונים וניהול סכימות בסביבת ענן.' },
  { title: 'אנליסט/ית BI', body: 'מדדים, מודלים סמנטיים ודשבורדים שמובילים להחלטה.' },
  { title: 'אבטחת מידע ל־AI', body: 'מודל איומים למערכות מבוססות מודל, בקרות גישה ותגובה לאירועים.' },
  { title: 'מנהל/ת מוצר דאטה', body: 'מוצרי נתונים, ממשל, מדידה וניהול מחזור חיים.' },
];

const STEPS = [
  { title: 'התחברות עם Google', body: 'כניסה בחשבון Google בלבד. אין סיסמאות מקומיות ואין הרשמה עצמאית.' },
  { title: 'אישור הסופר אדמין', body: 'כל חשבון חדש ממתין לאישור. עד לאישור אין גישה לשום חומר לימוד.' },
  { title: 'שיוך למסלול ולמודל', body: 'הסופר אדמין מחבר את הלומד למסלול ההכשרה שלו ולמודל ה־AI שילווה אותו.' },
  { title: 'למידה ומעקב', body: 'היחידות נפתחות לפי סדר, ההתקדמות נשמרת, והתוצרים נאספים לנכס מקצועי אחד.' },
];

const SECURITY_FACTS: Fact[] = [
  { icon: ShieldCheck, title: 'Google Sign-In בלבד', body: 'ללא סיסמאות מקומיות וללא מסלול הרשמה עצמאי.' },
  { icon: UserCheck, title: 'אישור גישה שרתי', body: 'ההרשאה נקבעת ב־Custom Claims ונאכפת גם בכללי Firestore.' },
  { icon: KeyRound, title: 'מפתחות מוצפנים', body: 'מפתח ה־AI האישי מוצפן ב־Cloud KMS ואינו נחשף לדפדפן.' },
  { icon: Layers3, title: 'הפרדת נתונים מלאה', body: 'כל לומד רואה את ההתקדמות, השיחות וההגדרות שלו בלבד.' },
  { icon: Cpu, title: 'App Check והגבלות קצב', body: 'כל קריאה לשרת נבדקת כמגיעה מהלקוח המורשה ומוגבלת בקצב.' },
  { icon: Search, title: 'ללא חשיפת חומר', body: 'חומרי הלימוד והמפתחות אינם נארזים לתוך קוד הדפדפן.' },
];

const NAV_LINKS = [
  { href: '#platform', label: 'הפלטפורמה' },
  { href: '#tracks', label: 'מסלולים' },
  { href: '#how', label: 'איך זה עובד' },
  { href: '#security', label: 'אבטחה' },
  { href: '#privacy', label: 'פרטיות' },
];

function GoogleButton({ onSignIn, className = '' }: { onSignIn: () => void; className?: string }) {
  return (
    <button className={`google-button ${className}`.trim()} onClick={onSignIn}>
      <span className="google-g">G</span>התחברות עם חשבון Google
    </button>
  );
}

export default function LandingPage({ error, onSignIn }: { error: string; onSignIn: () => void }) {
  // The header sits transparent over the hero and takes a solid background once
  // the visitor scrolls, so it never competes with the hero artwork.
  const [lifted, setLifted] = useState(false);
  // The privacy notice has to be readable before anyone signs in, and worth
  // linking to on its own, so it lives behind a hash rather than a scroll
  // anchor. Every other hash returns to the page.
  const [showPolicy, setShowPolicy] = useState(() => window.location.hash === '#privacy');

  useEffect(() => {
    const onScroll = () => setLifted(window.scrollY > 40);
    const onHashChange = () => {
      const wanted = window.location.hash === '#privacy';
      setShowPolicy(wanted);
      if (wanted) window.scrollTo({ top: 0 });
    };
    onScroll();
    onHashChange();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('hashchange', onHashChange);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('hashchange', onHashChange);
    };
  }, []);

  return (
    <div className="landing">
      {/* The bar is light-on-dark, which only works over the hero. The policy
          sits on the cream ground, so there the bar keeps its solid state from
          the first pixel instead of waiting for a scroll. */}
      <header className={`landing-bar ${lifted || showPolicy ? 'lifted' : ''}`.trim()}>
        <div className="landing-bar-inner">
          <a className="landing-mark" href="#top">
            <StudiumHeaderWordmark className="landing-header-wordmark" />
          </a>
          <nav className="landing-nav" aria-label="ניווט בדף">
            {NAV_LINKS.map((link) => <a key={link.href} href={link.href}>{link.label}</a>)}
          </nav>
          <button className="landing-bar-cta" onClick={onSignIn}>התחברות</button>
        </div>
      </header>

      {showPolicy ? (
        <main className="landing-policy-main">
          <div className="policy-shell">
            <a className="policy-back" href="#top"><ChevronsRight size={16} /> חזרה לדף הבית</a>
            <PrivacyPolicy />
            <div className="policy-shell-cta">
              <GoogleButton onSignIn={onSignIn} />
              {error && <p className="form-error landing-error">{error}</p>}
            </div>
          </div>
        </main>
      ) : (
      <main>
        <section className="landing-hero" id="top">
          <div className="aurora aurora-one" /><div className="aurora aurora-two" />
          <div className="landing-hero-inner">
            <StudiumLogo className="landing-logo" />
            <span className="eyebrow landing-eyebrow">פלטפורמת לימוד והכשרה לתפקידים מקצועיים</span>
            <h1>ללמוד לעומק.<br />להוביל בביטחון.</h1>
            <p className="landing-lede">
              סטודיום היא סביבת לימוד והכשרה מבוססת־מקור. כל לומד מקבל מסלול אישי, עוזר AI
              משלו ומעקב התקדמות שנאכף בשרת — הכול בגישה מאושרת בלבד.
            </p>
            <div className="landing-hero-actions">
              <GoogleButton onSignIn={onSignIn} className="landing-primary" />
              <a className="landing-ghost" href="#platform">מה יש במערכת <ChevronLeft size={17} /></a>
            </div>
            {error && <p className="form-error landing-error">{error}</p>}
            <p className="landing-hero-note">לאחר ההתחברות, חשבונות חדשים ממתינים לאישור הסופר אדמין.</p>
            {/* Section 11 of the Privacy Protection Law requires the notice at
                the point of collection, which is this button — not a link
                buried in a footer. */}
            <aside className="collection-notice">
              <ShieldCheck size={16} />
              <p>
                בהתחברות נאספים מחשבון Google שלך דוא״ל, שם ותמונה, ובהמשך נשמרים ההתקדמות,
                הטיוטות והשיחות שתיצור. המידע משמש להפעלת הלמידה ולבקרת גישה ונשמר ב־Google Cloud
                בישראל; רק בשימוש בעוזר ה־AI הוא מועבר ל־OpenRouter ולספק המודל שתבחר. המסירה
                אינה חובה חוקית, אך בלעדיה לא ניתן לספק את השירות. בכל עת תוכל לעיין במידע,
                לתקן אותו או למחוק את החשבון. <a href="#privacy">מדיניות הפרטיות המלאה</a>
              </p>
            </aside>
          </div>
        </section>

        <section className="landing-stats" aria-label="נתוני המערכת">
          <div className="landing-stats-inner">
            {STATS.map((stat) => (
              <article key={stat.label}>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
                <small>{stat.note}</small>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-section" id="platform" aria-labelledby="platform-title">
          <div className="landing-section-inner">
            <div className="landing-section-head">
              <span className="eyebrow">הפלטפורמה</span>
              <h2 id="platform-title">מה זה סטודיום</h2>
              <p>
                לא ספריית שיעורים ולא צ׳אט כללי. סביבה אחת שמחזיקה את חומר הלימוד, את
                ההתקדמות ואת הייעוץ האישי — עם מקור מאחורי כל טענה.
              </p>
            </div>
            <div className="landing-grid pillars">
              {PILLARS.map(({ icon: Icon, title, body }) => (
                <article key={title}>
                  <span className="landing-icon"><Icon size={21} /></span>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="landing-section tinted" id="tracks" aria-labelledby="tracks-title">
          <div className="landing-section-inner">
            <div className="landing-section-head">
              <span className="eyebrow">מסלולים</span>
              <h2 id="tracks-title">מסלולי הכשרה לתפקידים</h2>
              <p>
                כל מסלול מכין לתפקיד אחד מקצה לקצה. הסופר אדמין משייך כל לומד למסלול
                הרלוונטי לו, ומסלולים נוספים נבנים לפי הצורך הארגוני.
              </p>
            </div>

            <article className="track-feature">
              <div className="track-feature-copy">
                <span className="track-flag open"><Sparkles size={14} /> פתוח להרשמה</span>
                <h3>{ACTIVE_TRACK.title}</h3>
                <p>{ACTIVE_TRACK.summary}</p>
                <ul className="track-meta">
                  <li><Target size={15} /> 8 שבועות · 40 יחידות</li>
                  <li><BookOpen size={15} /> 32 שיעורים ו־8 מעבדות</li>
                  <li><Activity size={15} /> שער שליטה בכל יחידה</li>
                </ul>
                <GoogleButton onSignIn={onSignIn} className="track-cta" />
              </div>
              <ol className="track-weeks">
                {ACTIVE_TRACK.weeks.map((week, index) => (
                  <li key={week}><span>{index + 1}</span>{week}</li>
                ))}
              </ol>
            </article>

            <div className="landing-grid planned">
              {PLANNED_TRACKS.map((track) => (
                <article key={track.title}>
                  <span className="track-flag">בפיתוח</span>
                  <h3>{track.title}</h3>
                  <p>{track.body}</p>
                </article>
              ))}
            </div>
            <p className="landing-footnote">מסלולים נוספים נבנים לפי הצורך הארגוני.</p>
          </div>
        </section>

        <section className="landing-section" id="how" aria-labelledby="how-title">
          <div className="landing-section-inner">
            <div className="landing-section-head">
              <span className="eyebrow">התהליך</span>
              <h2 id="how-title">איך זה עובד</h2>
              <p>ארבעה צעדים מהכניסה הראשונה ועד היחידה הראשונה.</p>
            </div>
            <ol className="landing-steps">
              {STEPS.map((step, index) => (
                <li key={step.title}>
                  <span className="step-number">{index + 1}</span>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="landing-section dark" id="security" aria-labelledby="security-title">
          <div className="landing-section-inner">
            <div className="landing-section-head">
              <span className="eyebrow">בקרה</span>
              <h2 id="security-title">גישה מאושרת, שליטה מלאה</h2>
              <p>
                המערכת סגורה כברירת מחדל. הסופר אדמין מאשר או חוסם חשבונות, משייך לומדים
                למסלולים, וקובע את מודל ה־AI שמלווה כל לומד.
              </p>
            </div>
            <div className="landing-grid security">
              {SECURITY_FACTS.map(({ icon: Icon, title, body }) => (
                <article key={title}>
                  <span className="landing-icon dark"><Icon size={19} /></span>
                  <div>
                    <h3>{title}</h3>
                    <p>{body}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="landing-close" aria-labelledby="close-title">
          <div className="landing-close-inner">
            <h2 id="close-title">מוכן להתחיל?</h2>
            <p>התחבר בחשבון Google. הסופר אדמין יאשר את החשבון וישייך אותך למסלול שלך.</p>
            <GoogleButton onSignIn={onSignIn} className="landing-primary" />
            {error && <p className="form-error landing-error">{error}</p>}
          </div>
        </section>
      </main>
      )}

      <footer className="landing-footer">
        <div className="landing-footer-inner">
          {/* The same mark the header carries: the letterforms alone on the
              olive ground, with no cream panel behind them. The tagline below
              is set in the identical white the letterforms are drawn in. */}
          <div className="landing-mark static">
            <StudiumHeaderWordmark className="landing-header-wordmark" />
          </div>
          <p className="landing-footer-tagline">LEARN • PRACTICE • LEAD</p>
          <a className="landing-footer-link" href="#privacy">מדיניות פרטיות</a>
          <p className="landing-disclaimer">
            חומרי הלימוד עוסקים בדומיין מס סינתטי. הם אינם מתעדים מידע, סכימות, הרשאות או
            נהלים אמיתיים של ארגון כלשהו.
          </p>
        </div>
      </footer>
    </div>
  );
}
